import React, { useEffect, useState } from 'react';
import { Instagram, Copy, X, CheckCircle2, Gift, ArrowRight } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const INSTAGRAM_URL = 'https://instagram.com/trucrunch.in';
const CODE = 'INSTA100';

// A two-step popup shown BEFORE the customer places the order.
// Step 1: ask them to follow us on Instagram — no code shown.
// Step 2: after they click "Follow on Instagram" (opens IG in a new tab), reveal the code + Copy button.
// Once revealed, we set localStorage so the popup won't nag them again on this browser.
const FollowInstagramPopup = ({ storageKey = 'trucrunch_insta_popup', autoOpenDelayMs = 900, onCodeReady }) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Only permanently silence the popup once the user actually followed us.
    // Casual dismissals (X / "Maybe later" / backdrop) should NOT silence it forever —
    // otherwise a returning customer never sees the ₹100 offer again.
    const stored = localStorage.getItem(storageKey);
    if (stored === 'followed') return;
    const t = setTimeout(() => setOpen(true), autoOpenDelayMs);
    return () => clearTimeout(t);
  }, [storageKey, autoOpenDelayMs]);

  const close = () => setOpen(false); // NO localStorage write — reappears next visit

  const dismissForever = () => {
    // Only used after code was revealed (step 2). Marks 'followed' so we don't nag repeat visitors.
    localStorage.setItem(storageKey, 'followed');
    setOpen(false);
  };

  const goToInstagram = () => {
    window.open(INSTAGRAM_URL, '_blank', 'noopener,noreferrer');
    // Reveal code after they leave for IG + reward in-game booster
    setStep(2);
    localStorage.setItem(storageKey, 'followed');
    localStorage.setItem('trucrunch_follower', '1');
    if (onCodeReady) onCodeReady(CODE);
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(CODE);
      setCopied(true);
      toast({ title: 'Code copied!', description: 'Paste INSTA100 in the promo code field.' });
      setTimeout(() => setCopied(false), 2200);
    } catch (err) {
      console.warn('[insta popup] clipboard write failed', err);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" onClick={step === 1 ? close : dismissForever}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative bg-[#FFF4E6] rounded-3xl crunch-shadow max-w-md w-full overflow-hidden"
      >
        <button onClick={step === 1 ? close : dismissForever} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white flex items-center justify-center crunch-shadow-sm z-10">
          <X size={18} />
        </button>

        <div className="bg-gradient-to-r from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white text-center py-8 px-6 relative overflow-hidden">
          <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/10" />
          <div className="absolute -bottom-10 -left-10 w-32 h-32 rounded-full bg-white/10" />
          <div className="relative">
            <Instagram size={40} className="mx-auto" />
            <div className="text-[10px] uppercase tracking-widest mt-2 opacity-90">Exclusive Offer</div>
            <div className="font-display text-3xl md:text-4xl mt-1 uppercase leading-[0.95]">
              {step === 1 ? (<>Follow us,<br />get ₹100 off.</>) : (<>Thanks for<br />following!</>)}
            </div>
          </div>
        </div>

        {step === 1 && (
          <div className="p-6 md:p-7">
            <p className="font-brico text-base opacity-80">
              Follow <span className="font-bold">@trucrunch.in</span> on Instagram and unlock a <span className="font-bold">₹100 OFF</span> code you can use on this order (min. ₹499, once per user).
            </p>

            <button
              onClick={goToInstagram}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-4 text-white font-brico font-bold uppercase tracking-widest text-sm bg-gradient-to-r from-[#F58529] via-[#DD2A7B] to-[#8134AF] hover:-translate-y-0.5 transition-transform"
            >
              <Instagram size={18} /> Follow @trucrunch.in <ArrowRight size={16} />
            </button>

            <p className="mt-3 font-brico text-xs text-center opacity-60">
              Your code will be revealed after you click Follow.
            </p>

            <button onClick={close} className="mt-3 w-full text-xs font-brico opacity-60 hover:opacity-100 underline">
              Maybe later
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="p-6 md:p-7">
            <p className="font-brico text-base opacity-80">
              Here's your ₹100 off code. Paste it in the <span className="font-bold">promo code</span> field on this page.
            </p>

            <div className="mt-5 rounded-2xl border-2 border-dashed border-[#1a1a1a]/40 bg-white p-4 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest opacity-70 flex items-center gap-1"><Gift size={12} /> Your Code</div>
                <div className="font-display text-3xl mt-1 font-mono tracking-wider">{CODE}</div>
              </div>
              <button onClick={copyCode} className="btn-chunky primary text-xs">
                {copied ? <><CheckCircle2 size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
              </button>
            </div>

            <button
              onClick={dismissForever}
              className="mt-5 btn-chunky bg-[#1a1a1a] text-[#FFF4E6] w-full justify-center"
            >
              Continue to checkout
            </button>

            <p className="mt-4 text-[10px] font-brico opacity-60 text-center">
              ₹100 flat off · minimum order ₹499 · one use per user.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FollowInstagramPopup;
